import { useState, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { format } from "date-fns";
import { toast } from "sonner";
import { Check, Loader2, Anchor, ArrowRight, ArrowLeft, CalendarIcon } from "lucide-react";
import { API, SERVICES } from "@/lib/site";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";

const TIMES = ["Morning (8–11 AM)", "Midday (11 AM–2 PM)", "Afternoon (2–5 PM)", "Flexible / Any time"];
const inputCls =
  "bg-white/[0.03] border-white/15 rounded-none h-12 text-white placeholder:text-white/30 focus-visible:ring-1 focus-visible:ring-gold focus-visible:border-gold";
const initial = { name: "", email: "", phone: "", service: "", boat_type: "", preferred_time: "", message: "" };
const STEPS = ["Your Boat", "Schedule", "Details"];

export default function BookingWizard({ prefill }) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(initial);
  const [date, setDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!prefill) return;
    setForm((f) => ({
      ...f,
      ...(prefill.boat_type ? { boat_type: prefill.boat_type } : {}),
      ...(prefill.service ? { service: prefill.service } : {}),
      ...(prefill.message ? { message: prefill.message } : {}),
    }));
    setDone(false);
    setStep(0);
    toast.success("Your selection is attached — just add your details.");
  }, [prefill]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const next = () => {
    if (step === 0 && !form.service) {
      toast.error("Please choose a service to continue.");
      return;
    }
    setStep((s) => Math.min(2, s + 1));
  };
  const back = () => setStep((s) => Math.max(0, s - 1));

  const submit = async () => {
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill in your name, email and phone.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/bookings`, { ...form, preferred_date: date ? format(date, "PPP") : "" });
      setDone(true);
      toast.success("Request sent — check your inbox for a confirmation.");
    } catch (e) {
      toast.error("Something went wrong. Please call us at (909) 493-4950.");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setForm(initial);
    setDate(null);
    setStep(0);
    setDone(false);
  };

  return (
    <section id="booking" className="relative bg-black py-24 md:py-32 border-t border-white/10" data-testid="booking-section">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
          className="lg:col-span-5"
        >
          <p className="mb-6 text-xs uppercase tracking-mega text-gold">Book Now</p>
          <h2 className="font-serif font-light text-5xl md:text-7xl text-white leading-[0.9] tracking-tight">
            Request a<br />
            <span className="text-gold-gradient italic">Quote</span>
          </h2>
          <p className="mt-8 max-w-md text-base md:text-lg font-light text-white/60 leading-relaxed">
            Three quick steps. We'll confirm availability and pricing personally — usually within a day.
          </p>
          <div className="mt-12 space-y-4">
            {["No obligation quote", "Confirmation emailed instantly", "Insured & vetted detailers"].map((t) => (
              <div key={t} className="flex items-center gap-3 text-white/70">
                <Anchor size={16} className="text-gold" />
                <span className="text-sm tracking-wide">{t}</span>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.1 }}
          className="lg:col-span-7"
        >
          {done ? (
            <div className="flex flex-col items-center justify-center text-center border border-gold/30 bg-white/[0.02] py-24 px-8" data-testid="booking-success">
              <div className="flex h-16 w-16 items-center justify-center rounded-full border border-gold text-gold mb-6">
                <Check size={28} />
              </div>
              <h3 className="font-serif text-3xl md:text-4xl text-white">Request received.</h3>
              <p className="mt-4 max-w-sm text-white/60 font-light">
                Thank you — a confirmation is on its way to your inbox. We'll be in touch shortly.
              </p>
              <button onClick={reset} className="mt-8 text-xs uppercase tracking-[0.25em] text-gold hover:text-gold-light transition-colors" data-testid="booking-new-request">
                Submit another request
              </button>
            </div>
          ) : (
            <div className="border border-white/10 bg-white/[0.02] p-6 md:p-10">
              {/* Progress */}
              <div className="mb-10 flex items-center gap-3">
                {STEPS.map((label, i) => (
                  <div key={label} className="flex items-center gap-3 flex-1">
                    <div className="flex items-center gap-3">
                      <span className={`flex h-8 w-8 items-center justify-center border text-xs ${i <= step ? "border-gold text-gold" : "border-white/20 text-white/30"}`}>
                        {i < step ? <Check size={14} /> : i + 1}
                      </span>
                      <span className={`hidden sm:block text-[11px] uppercase tracking-[0.2em] ${i <= step ? "text-white/80" : "text-white/30"}`}>
                        {label}
                      </span>
                    </div>
                    {i < STEPS.length - 1 && <span className={`h-px flex-1 ${i < step ? "bg-gold" : "bg-white/15"}`} />}
                  </div>
                ))}
              </div>

              <AnimatePresence mode="wait">
                <motion.div
                  key={step}
                  initial={{ opacity: 0, x: 24 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -24 }}
                  transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                >
                  {step === 0 && (
                    <div className="space-y-6" data-testid="wizard-step-1">
                      <div>
                        <label className="mb-3 block text-xs uppercase tracking-[0.2em] text-white/50">Which service?</label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {SERVICES.map((s) => (
                            <button
                              key={s.id}
                              onClick={() => setForm((f) => ({ ...f, service: s.title }))}
                              className={`text-left p-4 border transition-colors duration-200 ${form.service === s.title ? "border-gold bg-gold/[0.06] text-white" : "border-white/15 text-white/70 hover:border-white/40"}`}
                              data-testid={`wizard-service-${s.id}`}
                            >
                              <span className="block font-serif text-lg">{s.title}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <label className="mb-2 block text-xs uppercase tracking-[0.2em] text-white/50">Boat / Vessel</label>
                        <Input className={inputCls} value={form.boat_type} onChange={set("boat_type")} placeholder="e.g. 32ft Sea Ray, pontoon, speed boat" data-testid="wizard-boat" />
                      </div>
                    </div>
                  )}

                  {step === 1 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="wizard-step-2">
                      <div>
                        <label className="mb-2 block text-xs uppercase tracking-[0.2em] text-white/50">Preferred Date</label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <button type="button" className={`${inputCls} w-full flex items-center justify-between px-3 text-left ${date ? "text-white" : "text-white/30"}`} data-testid="wizard-date-trigger">
                              {date ? format(date, "PPP") : "Pick a date"}
                              <CalendarIcon size={16} className="text-gold" />
                            </button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0 bg-[#0A0A0A] border-white/15 rounded-none" align="start">
                            <Calendar mode="single" selected={date} onSelect={setDate} disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))} initialFocus className="text-white" />
                          </PopoverContent>
                        </Popover>
                      </div>
                      <div>
                        <label className="mb-2 block text-xs uppercase tracking-[0.2em] text-white/50">Preferred Time</label>
                        <Select value={form.preferred_time} onValueChange={(v) => setForm((f) => ({ ...f, preferred_time: v }))}>
                          <SelectTrigger className={inputCls} data-testid="wizard-time"><SelectValue placeholder="Any time" /></SelectTrigger>
                          <SelectContent className="bg-[#0A0A0A] border-white/15 text-white rounded-none">
                            {TIMES.map((t) => (<SelectItem key={t} value={t} className="focus:bg-gold focus:text-black">{t}</SelectItem>))}
                          </SelectContent>
                        </Select>
                      </div>
                      <p className="md:col-span-2 text-xs text-white/40">No date locked yet? Leave it blank — we'll suggest the soonest opening.</p>
                    </div>
                  )}

                  {step === 2 && (
                    <div className="space-y-6" data-testid="wizard-step-3">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="mb-2 block text-xs uppercase tracking-[0.2em] text-white/50">Full Name *</label>
                          <Input className={inputCls} value={form.name} onChange={set("name")} placeholder="Jane Harbor" data-testid="wizard-name" />
                        </div>
                        <div>
                          <label className="mb-2 block text-xs uppercase tracking-[0.2em] text-white/50">Phone *</label>
                          <Input className={inputCls} value={form.phone} onChange={set("phone")} placeholder="(555) 000-0000" data-testid="wizard-phone" />
                        </div>
                      </div>
                      <div>
                        <label className="mb-2 block text-xs uppercase tracking-[0.2em] text-white/50">Email *</label>
                        <Input className={inputCls} type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" data-testid="wizard-email" />
                      </div>
                      <div>
                        <label className="mb-2 block text-xs uppercase tracking-[0.2em] text-white/50">Notes</label>
                        <Textarea className={`${inputCls} h-28 py-3`} value={form.message} onChange={set("message")} placeholder="Marina, boat condition, anything we should know…" data-testid="wizard-message" />
                      </div>
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>

              {/* Nav buttons */}
              <div className="mt-10 flex items-center justify-between gap-4">
                <button
                  onClick={back}
                  disabled={step === 0}
                  className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-white/60 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  data-testid="wizard-back"
                >
                  <ArrowLeft size={14} /> Back
                </button>

                {step < 2 ? (
                  <button
                    onClick={next}
                    className="group inline-flex items-center gap-2 bg-gold text-black px-10 py-4 text-xs uppercase tracking-[0.25em] hover:bg-gold-light transition-colors duration-300"
                    data-testid="wizard-next"
                  >
                    Continue
                    <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </button>
                ) : (
                  <button
                    onClick={submit}
                    disabled={loading}
                    className="inline-flex items-center gap-2 bg-gold text-black px-10 py-4 text-xs uppercase tracking-[0.25em] hover:bg-gold-light transition-colors duration-300 disabled:opacity-60"
                    data-testid="wizard-submit"
                  >
                    {loading ? (<><Loader2 size={16} className="animate-spin" /> Sending</>) : "Send Request"}
                  </button>
                )}
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
}
